import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getStoryblokApi } from '@storyblok/react'
import type { ISbStoryData } from '@storyblok/js'

import { env } from '@percent/cause-dashboard/env'
import { isStoryblokPreviewSearch } from '@percent/cause-dashboard/storyblok/helpers/isStoryblokPreviewSearch'
import {
  SAVINGS_CALCULATOR_STORY_ID,
  SavingsPartner
} from '@percent/cause-dashboard/common/components/savingsCalculator/savingsCalculator.config'
import type { SavingsCalculatorConfig } from '@percent/cause-dashboard/storyblok/types/storyblok-components'
import { buildSavingsPartnersFromStory } from './buildSavingsPartnersFromStory'
import '../storyblokInit'

const RESOLVE_RELATIONS = ['savings_calculator_item.product_offer']

export type SavingsCalculatorStoryState = {
  partners: SavingsPartner[]
  loading: boolean
  error: Error | null
  isPreview: boolean
  refetch: () => void
}

export function useSavingsCalculatorStory(): SavingsCalculatorStoryState {
  const [searchParams] = useSearchParams()
  const isPreview = isStoryblokPreviewSearch(searchParams)

  const [story, setStory] = useState<ISbStoryData<SavingsCalculatorConfig> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [fetchId, setFetchId] = useState(0)

  const refetch = useCallback(() => {
    setFetchId(n => n + 1)
  }, [])

  useEffect(() => {
    const publishedToken = env.NX_PUBLIC_STORYBLOK_PUBLIC_ACCESS_TOKEN
    const previewToken = env.NX_PUBLIC_STORYBLOK_PREVIEW_ACCESS_TOKEN

    if (isPreview && !previewToken) {
      setStory(null)
      setError(new Error('Storyblok env missing for preview: NX_PUBLIC_STORYBLOK_PREVIEW_ACCESS_TOKEN'))
      setLoading(false)
      return
    }

    if (!isPreview && !publishedToken) {
      setStory(null)
      setError(new Error('Storyblok env missing: NX_PUBLIC_STORYBLOK_PUBLIC_ACCESS_TOKEN'))
      setLoading(false)
      return
    }

    const token = isPreview ? previewToken : publishedToken
    const version = isPreview ? 'draft' : 'published'

    let cancelled = false
    setLoading(true)
    setError(null)

    const api = getStoryblokApi()
    api
      .get(`cdn/stories/${SAVINGS_CALCULATOR_STORY_ID}`, {
        version,
        resolve_relations: RESOLVE_RELATIONS,
        token
      })
      .then(res => {
        if (cancelled) return
        const s = res.data?.story as ISbStoryData<SavingsCalculatorConfig> | undefined
        if (!s?.content || s.content.component !== 'savings_calculator_config') {
          setError(new Error('Storyblok: invalid savings calculator story response'))
          setStory(null)
          return
        }
        setStory(s)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err : new Error(String(err)))
        setStory(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [fetchId, isPreview])

  const partners = useMemo(() => buildSavingsPartnersFromStory(story), [story])

  return { partners, loading, error, isPreview, refetch }
}
